import { useEffect, useState } from "react";
import { useAdminStore } from "@/lib/stores/admin-store";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AlertCircle, Search, Trash2, ShieldCheck, UserCheck, UserX, Phone, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Admin Users Management Page
 * Enhanced with user cards and delete functionality
 */
export function UsersPage() {
  const { 
    isInitialized, 
    error, 
    users,
    fetchUsers,
    deleteUser,
    updateUserRole
  } = useAdminStore();
  
  const [search, setSearch] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'creator' | 'admin'>('all');

  // Refresh data when component mounts
  useEffect(() => {
    if (isInitialized) {
      fetchUsers().catch(err => 
        console.error("[AdminUsers] Error refreshing users:", err)
      );
    }
  }, [isInitialized, fetchUsers]);

  // Filter and search users
  const filteredUsers = users.filter(user => {
    // First filter by role
    if (roleFilter !== 'all' && user.role !== roleFilter) {
      return false;
    }
    
    // Then filter by search term
    if (!search) return true;
    
    const searchTerm = search.toLowerCase();
    return (
      (user.email && user.email.toLowerCase().includes(searchTerm)) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm)) ||
      (user.phone_number && user.phone_number.includes(searchTerm)) ||
      (user.id && user.id.toLowerCase().includes(searchTerm))
    );
  });

  // Handle user deletion with confirmation
  const handleDeleteUser = async (userId: string) => {
    try {
      setIsProcessing(true);
      await deleteUser(userId);
      setShowConfirmDelete(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (userId: string, newRole: 'user' | 'creator' | 'admin') => {
    try {
      setIsProcessing(true);
      await updateUserRole(userId, newRole);
    } catch (error) {
      console.error("Failed to update user role:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Full page loading state
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Errors banner */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-400 mr-3" />
            <div>
              <p className="text-sm text-red-300 font-medium">
                Error loading users
              </p>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative w-full sm:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-sand-400" />
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-earth-700 border border-earth-600 text-sand-200 placeholder-sand-500 block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-sand-400"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant={roleFilter === 'all' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setRoleFilter('all')}
            className={roleFilter === 'all' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            All
          </Button>
          <Button
            variant={roleFilter === 'user' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setRoleFilter('user')}
            className={roleFilter === 'user' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Users
          </Button>
          <Button
            variant={roleFilter === 'creator' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setRoleFilter('creator')}
            className={roleFilter === 'creator' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Creators
          </Button>
          <Button
            variant={roleFilter === 'admin' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setRoleFilter('admin')}
            className={roleFilter === 'admin' ? 'bg-sand-400 text-earth-900' : 'bg-earth-700 text-sand-300'}
          >
            Admins
          </Button>
        </div>
      </div>

      {/* User Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map((user) => (
          <div key={user.id} className="bg-earth-700 rounded-lg shadow-lg overflow-hidden">
            <div className="p-4">
              {/* Profile Picture */}
              <div className="flex items-center justify-center mb-4">
                {user.profile_image ? (
                  <img 
                    src={user.profile_image} 
                    alt={user.full_name || 'User'} 
                    className="h-20 w-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-earth-600 flex items-center justify-center">
                    <User className="h-10 w-10 text-sand-400" />
                  </div>
                )}
              </div>
              
              {/* User Info */}
              <div className="text-center mb-4">
                <h3 className="text-lg font-medium text-sand-100">
                  {user.full_name || 'Unnamed User'}
                </h3>
                <div className="flex items-center justify-center mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'admin' 
                      ? 'bg-purple-900/30 text-purple-300' 
                      : user.role === 'creator'
                      ? 'bg-blue-900/30 text-blue-300'
                      : 'bg-green-900/30 text-green-300'
                  }`}>
                    {user.role}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sand-300 text-sm">
                  <Mail className="h-4 w-4 mr-2" />
                  <span className="truncate">{user.email}</span>
                </div>
                {user.phone_number && (
                  <div className="flex items-center text-sand-300 text-sm">
                    <Phone className="h-4 w-4 mr-2" />
                    <span>{user.phone_number}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end border-t border-earth-600 pt-4">
                {showConfirmDelete === user.id ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isProcessing}
                      onClick={() => handleDeleteUser(user.id)}
                      className="bg-red-500/20 text-red-300 hover:bg-red-500/30"
                    >
                      Confirm Delete
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isProcessing}
                      onClick={() => setShowConfirmDelete(null)}
                      className="bg-earth-600 text-sand-300"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowConfirmDelete(user.id)}
                    className="bg-red-500/20 text-red-300 hover:bg-red-500/30"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-sand-500/20 mx-auto mb-3" />
          <p className="text-sand-400">No users found</p>
          <p className="text-sm text-sand-500 mt-1">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
} 